import type Phaser from 'phaser';

import {
  animate,
  BLOOD,
  dot,
  EMBER,
  EMBER_DARK,
  ellipseDots,
  fadeAfter,
  type Feet,
  GOLD,
  GOLD_TIP,
  groundRing,
  METAL,
  METAL_DARK,
  METAL_LIGHT,
  ORANGE,
  over,
  WHITE,
} from './fx-kit.js';
import { crack } from './spell-effects-ground.js';
import { puffSmoke } from './smoke-puff.js';

/**
 * The looks of the Warrior's grimoire past the first three spells (ADR 0048),
 * in the style of `spell-effects.ts`: code-drawn, whole pixels, palette
 * colours, a counter from 0 to 1. Purely visual.
 */

const TILE = 16;

/** Sparks flying out of a point, cooling as they go: the moment a blow lands. */
function burst(
  graphics: Phaser.GameObjects.Graphics,
  cx: number,
  cy: number,
  q: number,
  rays: number,
  reach: number,
  colors: readonly [number, number, number],
  tilt = 0,
): void {
  const inner = 2 + q * reach;
  const tail = Math.max(1, Math.round(3 * (1 - q)));
  const color = q < 0.35 ? colors[0] : q < 0.7 ? colors[1] : colors[2];
  for (let ray = 0; ray < rays; ray += 1) {
    const angle = tilt + (ray / rays) * Math.PI * 2;
    const scale = ray % 2 === 0 ? 1 : 0.65;
    for (let step = 0; step < tail; step += 1) {
      const r = (inner + step) * scale;
      dot(graphics, cx + Math.cos(angle) * r, cy + Math.sin(angle) * r * 0.8, color);
    }
  }
}

/**
 * Charge: streaks of wind from where the warrior stood to where they land,
 * dust kicked up at both ends, and a white impact on the target.
 */
export function charge(scene: Phaser.Scene, from: Feet, to: Feet | undefined, target?: Feet): void {
  const end = to ?? target ?? from;
  const dx = end.x - from.x;
  const dy = end.y - from.y;
  const length = Math.hypot(dx, dy);
  puffSmoke(scene, { x: Math.round(from.x), y: Math.round(from.y) }, over(from));
  if (length >= 1) {
    const ux = dx / length;
    const uy = dy / length;
    // Perpendicular: the three streaks run side by side along the bound.
    const px = -uy;
    const py = ux;
    const depth = Math.max(over(from), over(end));
    animate(scene, depth, 420, (graphics, p) => {
      graphics.setAlpha(fadeAfter(p, 0.35));
      const head = Math.min(1, p / 0.3) * length;
      const tail = Math.max(0, (p - 0.2) / 0.8) * length;
      for (const [offset, lift, color] of [
        [-3, 6, METAL_LIGHT],
        [0, 11, WHITE],
        [3, 16, METAL_LIGHT],
      ] as const) {
        for (let along = tail; along <= head; along += 1) {
          const bright = head - along < 4;
          dot(
            graphics,
            from.x + ux * along + px * offset,
            from.y - lift + uy * along + py * offset * 0.5,
            bright ? WHITE : color,
          );
        }
      }
    });
  }
  const hit = target ?? end;
  const cx = Math.round(hit.x);
  const cy = Math.round(hit.y) - 11;
  const tilt = Math.random() * Math.PI;
  animate(
    scene,
    over(hit),
    380,
    (graphics, p) => {
      graphics.setAlpha(fadeAfter(p, 0.5));
      if (p < 0.25) {
        graphics.fillStyle(WHITE, 1);
        graphics.fillRect(cx - 2, cy - 2, 5, 5);
      }
      burst(graphics, cx, cy, p, 8, 11, [WHITE, METAL_LIGHT, METAL], tilt);
    },
    150,
  );
  if (to !== undefined) {
    scene.time.delayedCall(160, () => {
      puffSmoke(scene, { x: Math.round(to.x), y: Math.round(to.y) }, over(to));
    });
  }
}

/** Rend: two red cuts across the target, then drops sprayed off the wound. */
export function rend(scene: Phaser.Scene, feet: Feet): void {
  const cx = Math.round(feet.x);
  const cy = Math.round(feet.y) - 11;
  const drops = Array.from({ length: 7 }, () => ({
    angle: -Math.PI / 2 + (Math.random() - 0.5) * 2.2,
    speed: 6 + Math.random() * 6,
  }));
  animate(scene, over(feet), 480, (graphics, p) => {
    graphics.setAlpha(fadeAfter(p, 0.55));
    const half = 8;
    for (const [shift, delay] of [
      [-2, 0],
      [3, 0.12],
    ] as const) {
      const reach = Math.min(1, Math.max(0, (p - delay) / 0.25));
      const length = Math.round(half * 2 * reach);
      for (let step = 0; step <= length; step += 1) {
        const t = step / (half * 2);
        // Top left to bottom right, the other way from the power strike.
        const x = cx - half + t * half * 2 + shift;
        const y = cy - half + t * half * 2 - shift;
        const tip = step >= length - 1;
        dot(graphics, x, y, tip ? WHITE : BLOOD);
        dot(graphics, x + 1, y, tip ? BLOOD : EMBER_DARK);
      }
    }
    if (p > 0.25) {
      const q = (p - 0.25) / 0.75;
      for (const drop of drops) {
        const x = cx + Math.cos(drop.angle) * drop.speed * q;
        const y = cy + Math.sin(drop.angle) * drop.speed * q + 14 * q * q;
        dot(graphics, x, y, q < 0.5 ? BLOOD : EMBER_DARK);
      }
    }
  });
}

/** Shield wall: a steel shield flashes up round the caster, then settles and fades. */
export function shieldWall(scene: Phaser.Scene, feet: Feet): void {
  const cx = Math.round(feet.x);
  const cy = Math.round(feet.y) - 12;
  const ring = (front: boolean) => (graphics: Phaser.GameObjects.Graphics, p: number) => {
    graphics.setAlpha(fadeAfter(p, 0.5));
    const grow = Math.min(1, p / 0.25);
    const rx = 5 + grow * 6;
    const ry = 8 + grow * 7;
    const flash = p < 0.3;
    const shimmer = Math.floor(p * 40);
    ellipseDots(graphics, cx, cy, rx, ry, front, (step) =>
      flash ? WHITE : (step + shimmer) % 6 === 0 ? WHITE : step % 2 === 0 ? METAL_LIGHT : METAL,
    );
    // The boss of the shield, a gold spot over the chest, in front only.
    if (front && p < 0.7) {
      graphics.fillStyle(p < 0.3 ? GOLD_TIP : GOLD, 1);
      graphics.fillRect(cx - 1, cy, 3, 3);
    }
  };
  groundRing(scene, { x: feet.x, y: feet.y }, 700, ring);
}

/** Shield bash: a flat steel impact on the target, and little stars popping over its head. */
export function shieldBash(scene: Phaser.Scene, feet: Feet): void {
  const cx = Math.round(feet.x);
  const cy = Math.round(feet.y) - 11;
  animate(scene, over(feet), 600, (graphics, p) => {
    graphics.setAlpha(fadeAfter(p, 0.6));
    if (p < 0.2) {
      graphics.fillStyle(WHITE, 1);
      graphics.fillRect(cx - 3, cy - 3, 7, 7);
      graphics.fillStyle(METAL_LIGHT, 1);
      graphics.fillRect(cx - 4, cy - 1, 9, 3);
    }
    burst(graphics, cx, cy, Math.min(1, p / 0.6), 6, 9, [WHITE, METAL_LIGHT, METAL_DARK]);
    if (p > 0.25) {
      const q = (p - 0.25) / 0.75;
      for (let star = 0; star < 3; star += 1) {
        const x = cx + (star - 1) * 6;
        const y = cy - 10 - q * 6 - (star === 1 ? 2 : 0);
        const color = Math.floor(q * 10 + star) % 2 === 0 ? GOLD_TIP : GOLD;
        dot(graphics, x, y, color);
        dot(graphics, x - 1, y, GOLD);
        dot(graphics, x + 1, y, GOLD);
        dot(graphics, x, y - 1, GOLD);
        dot(graphics, x, y + 1, GOLD);
      }
    }
  });
}

/** Taunt: red rings of a shout rolling over the ground out to three tiles. */
export function taunt(scene: Phaser.Scene, feet: Feet): void {
  const cx = Math.round(feet.x);
  const cy = Math.round(feet.y) - 3;
  const reach = 3 * TILE;
  for (let wave = 0; wave < 3; wave += 1) {
    const ring = (front: boolean) => (graphics: Phaser.GameObjects.Graphics, p: number) => {
      graphics.setAlpha(fadeAfter(p, 0.5));
      const rx = 4 + p * reach;
      const ry = rx * 0.5;
      ellipseDots(graphics, cx, cy, rx, ry, front, (step) =>
        p < 0.35 ? (step % 3 === 0 ? ORANGE : BLOOD) : step % 2 === 0 ? BLOOD : EMBER_DARK,
      );
    };
    groundRing(scene, feet, 620, ring, wave * 130);
  }
  // The shout itself: short red strokes off the head, like a cartoon yell.
  animate(scene, over(feet), 520, (graphics, p) => {
    graphics.setAlpha(fadeAfter(p, 0.5));
    const hy = Math.round(feet.y) - 26;
    const out = 3 + Math.round(p * 4);
    for (const side of [-1, 1]) {
      for (const [tilt, rise] of [
        [0.9, -2],
        [1, 1],
        [0.8, 4],
      ] as const) {
        for (let step = 0; step < 3; step += 1) {
          dot(
            graphics,
            cx + side * (out + step) * tilt,
            hy + rise - step * 0.5 * (rise < 0 ? 1 : -1),
            BLOOD,
          );
        }
      }
    }
  });
}

/** Rage: a red burst off the caster's body and embers rising round them. */
export function rage(scene: Phaser.Scene, feet: Feet): void {
  const cx = Math.round(feet.x);
  const cy = Math.round(feet.y) - 12;
  const tilt = Math.random() * Math.PI;
  animate(scene, over(feet), 520, (graphics, p) => {
    graphics.setAlpha(fadeAfter(p, 0.5));
    burst(graphics, cx, cy, p, 12, 14, [ORANGE, BLOOD, EMBER_DARK], tilt);
  });
  const ring = (front: boolean) => (graphics: Phaser.GameObjects.Graphics, p: number) => {
    graphics.setAlpha(fadeAfter(p, 0.4));
    const rx = 3 + p * 16;
    ellipseDots(graphics, cx, Math.round(feet.y) - 2, rx, rx * 0.45, front, (step) =>
      step % 2 === 0 ? BLOOD : EMBER,
    );
  };
  groundRing(scene, feet, 520, ring);
  const embers = Array.from({ length: 10 }, (_, index) => ({
    x: cx + Math.round(Math.cos((index / 10) * Math.PI * 2) * (5 + Math.random() * 4)),
    y: Math.round(feet.y) - 2 - Math.round(Math.random() * 10),
    rise: 12 + Math.random() * 10,
    delay: Math.random() * 0.35,
  }));
  animate(scene, over(feet), 800, (graphics, p) => {
    for (const ember of embers) {
      const q = (p - ember.delay) / (1 - ember.delay);
      if (q <= 0 || q >= 1) continue;
      dot(
        graphics,
        ember.x,
        ember.y - ember.rise * q,
        q < 0.4 ? ORANGE : q < 0.7 ? BLOOD : EMBER_DARK,
      );
    }
  });
}

/** Execute: a heavy blade falls straight down through the target and cracks the ground. */
export function execute(scene: Phaser.Scene, feet: Feet): void {
  const cx = Math.round(feet.x);
  const ground = Math.round(feet.y) - 2;
  const top = ground - 30;
  animate(scene, over(feet), 520, (graphics, p) => {
    graphics.setAlpha(fadeAfter(p, 0.55));
    const fall = Math.min(1, p / 0.22);
    const bottom = Math.round(top + (ground - top) * fall);
    for (let y = top; y <= bottom; y += 1) {
      const tip = bottom - y < 3;
      dot(graphics, cx - 2, y, tip ? GOLD_TIP : GOLD);
      dot(graphics, cx - 1, y, WHITE);
      dot(graphics, cx, y, tip ? WHITE : GOLD_TIP);
      dot(graphics, cx + 1, y, WHITE);
      dot(graphics, cx + 2, y, tip ? GOLD_TIP : GOLD);
    }
    if (fall >= 1) {
      const q = (p - 0.22) / 0.78;
      if (q < 0.2) {
        graphics.fillStyle(WHITE, 1);
        graphics.fillRect(cx - 4, ground - 1, 9, 3);
      }
      // Sparks spraying sideways off the ground, and two cracks either side.
      for (let spark = 0; spark < 8; spark += 1) {
        const side = spark % 2 === 0 ? -1 : 1;
        const x = cx + side * (3 + q * (8 + spark));
        const y = ground - (spark % 3) * 2 - 6 * q * (1 - q) * 4;
        dot(graphics, x, y, q < 0.4 ? GOLD_TIP : q < 0.7 ? GOLD : ORANGE);
      }
      crack(graphics, cx, ground, 0, 10, Math.min(1, q / 0.4), 1);
      crack(graphics, cx, ground, Math.PI, 10, Math.min(1, q / 0.4), 4);
    }
  });
}
